import { Stack } from '@mui/material';
import { useMemo, useState } from 'react';

import { Station } from 'domain/entities/Station';
import { useCaseSearchStation } from 'application/stations/useCases/useCaseSearchStation';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Breadcrumbs, ReactRouterLink, TableColumn, TableWithSearch } from 'targets/web/components';
import { OfflinePlaceholder, PageTitle } from 'targets/web/modules/dashboard/components';
import { configurationRoutes } from 'targets/web/modules/configuration/navigation/configurationRoutes';
import { useNetworkStatus } from 'targets/web/modules/dashboard/hooks/useNetworkStatus';

export const StationsView = () => {
  const t = useTranslationPrefix('configuration.stations');
  const tBreadcrumbs = useTranslationPrefix('configuration.breadcrumbs');
  const { isOffline } = useNetworkStatus();

  const [phrase, setPhrase] = useState('');
  const {
    items = [],
    total,
    isLoading,

    paging,
    changePage,
    changePageSize,

    sorting,
    changeSort,
  } = useCaseSearchStation({ phrase });

  const columns: TableColumn<Station>[] = useMemo(
    () => [
      {
        headerName: t('column_header.name'),
        field: 'name',
        sortable: true,
        width: '33%',
        renderCell: ({ id, name }) => (
          <ReactRouterLink to={`${id}`} variant="body1" color="primary">
            {name}
          </ReactRouterLink>
        ),
      },
      {
        headerName: t('column_header.parent'),
        field: 'parent',
        width: '33%',
        renderCell: ({ parent }) => parent?.code ?? '-',
      },
      {
        headerName: t('column_header.acronym'),
        field: 'code',
        sortable: true,
        width: '33%',
        renderCell: ({ code }) => code,
      },
    ],
    [t],
  );

  return (
    <>
      <Stack direction="row" width={1} justifyContent="flex-start" alignItems="center" gap={4}>
        <Stack direction="column" alignItems="flex-start" justifyContent="space-between" flex={1}>
          <Breadcrumbs
            items={[
              {
                label: tBreadcrumbs('configuration'),
                url: configurationRoutes.configuration,
              },
            ]}
          />

          <PageTitle title={t('title')} />
        </Stack>
      </Stack>

      <Stack width={1}>
        {isOffline && !items.length ? (
          <OfflinePlaceholder />
        ) : (
          <TableWithSearch
            onSearchPhraseChange={setPhrase}
            columns={columns}
            rows={items}
            count={total}
            rowsPerPageOptions={[10, 50, 100]}
            initialRowsPerPage={10}
            orderBy={sorting.sortBy}
            orderDirection={sorting.direction}
            initialPage={Math.round(paging.offset / paging.limit)}
            isLoading={isLoading}
            searchPhrasePlaceholder={t('placeholder')}
            onRowsPerPageChange={changePageSize}
            onPageChange={changePage}
            onSortChange={changeSort}
          />
        )}
      </Stack>
    </>
  );
};
